import React from 'react';
import { Button } from '@/components/ui/button';
import { ChevronRight } from 'lucide-react';

function SimpleList({ rows, k1, k2, empty, testid }) {
  if (!rows?.length) return <div className="text-xs text-zinc-400">{empty}</div>;
  const max = Math.max(...rows.map((r) => r[k2])) || 1;
  return (
    <ul className="space-y-1.5" data-testid={testid}>
      {rows.map((r, i) => (
        <li key={i} className="flex items-center gap-3 text-sm">
          <span className="w-40 truncate text-zinc-700">{r[k1]}</span>
          <span className="flex-1 bg-zinc-100 rounded-full h-2 overflow-hidden">
            <span
              className="block h-full bg-gradient-to-r from-[#0C2744] to-[#F15A22] rounded-full"
              style={{ width: `${(r[k2] / max) * 100}%` }}
            />
          </span>
          <span className="font-mono text-xs text-zinc-700 w-8 text-right">{r[k2]}</span>
        </li>
      ))}
    </ul>
  );
}

/** Referrals + schedule-drug risk for care pathway decisions. */
export default function ManagementCareRiskSection({ referrals, schedule, onOpen }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="mgmt-care-risk">
      <div className="rounded-2xl border border-zinc-200 bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Care pathway</div>
            <h3 className="text-lg font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }}>
              Referrals · {referrals?.total ?? 0}
            </h3>
          </div>
          <Button variant="outline" size="sm" onClick={() => onOpen('referrals')} data-testid="mgmt-referrals-drilldown">
            See all <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
        <SimpleList rows={(referrals?.by_doctor || []).slice(0, 5)} k1="doctor" k2="count"
          empty="No referrals in this period." testid="mgmt-referrals-bydoctor" />
        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-4 mb-1.5">Top reasons</div>
        <SimpleList rows={(referrals?.by_reason || []).slice(0, 5)} k1="reason" k2="count"
          empty="—" testid="mgmt-referrals-byreason" />
      </div>

      <div className="rounded-2xl border border-zinc-200 bg-white p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Pharmacovigilance</div>
            <h3 className="text-lg font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }}>
              Schedule-drug Rx · {schedule?.total ?? 0}
            </h3>
          </div>
          <Button variant="outline" size="sm" onClick={() => onOpen('schedule')} data-testid="mgmt-schedule-drilldown">
            See all <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {(schedule?.by_schedule || []).map((b) => (
            <span key={b.schedule} className="text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200 rounded-full px-2.5 py-0.5">
              Schedule {b.schedule} · {b.count}
            </span>
          ))}
        </div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5">By doctor</div>
        <SimpleList rows={(schedule?.by_doctor || []).slice(0, 5)} k1="doctor" k2="count" empty="None" testid="mgmt-sched-bydoctor" />
        <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-4 mb-1.5">By drug class</div>
        <SimpleList rows={(schedule?.by_class || []).slice(0, 5)} k1="class" k2="count" empty="None" testid="mgmt-sched-byclass" />
      </div>
    </div>
  );
}
